import React, { useState, useEffect } from 'react';
import { CheckCircle2, Circle, Plus, AlertTriangle, Clock } from 'lucide-react';
import { api } from '../services/api';
import styles from './TasksPage.module.css';

export default function TasksPage() {
  const [tasks, setTasks]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState('all');
  const [newTitle, setNewTitle] = useState('');
  const [adding, setAdding]   = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.get('/tasks/');
      setTasks(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const complete = async (id) => {
    await api.patch(`/tasks/${id}`, { status: 'done' });
    setTasks(p => p.map(t => t.id === id ? { ...t, status: 'done' } : t));
  };

  const addTask = async () => {
    if (!newTitle.trim()) return;
    setAdding(true);
    try {
      const t = await api.post('/tasks/', { title: newTitle });
      setTasks(p => [...p, { ...t, status: 'todo', priority: 'medium' }]);
      setNewTitle('');
    } finally {
      setAdding(false);
    }
  };

  const now = new Date();
  const filtered = tasks.filter(t => {
    if (filter === 'todo')    return t.status !== 'done';
    if (filter === 'done')    return t.status === 'done';
    if (filter === 'overdue') return t.status !== 'done' && t.due_date && new Date(t.due_date) < now;
    return true;
  });

  const overdueCnt = tasks.filter(t => t.status !== 'done' && t.due_date && new Date(t.due_date) < now).length;
  const todayCnt   = tasks.filter(t => {
    if (t.status === 'done' || !t.due_date) return false;
    const d = new Date(t.due_date);
    return d.toDateString() === now.toDateString();
  }).length;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>Task Manager</h1>

        {/* Stats */}
        <div className={styles.stats}>
          {overdueCnt > 0 && (
            <div className={`${styles.stat} ${styles.statRed}`}>
              <AlertTriangle size={13} />
              {overdueCnt} scaduti
            </div>
          )}
          {todayCnt > 0 && (
            <div className={`${styles.stat} ${styles.statAmber}`}>
              <Clock size={13} />
              {todayCnt} oggi
            </div>
          )}
          <div className={styles.stat}>
            <CheckCircle2 size={13} />
            {tasks.filter(t => t.status === 'done').length} completati
          </div>
        </div>
      </div>

      {/* Add task */}
      <div className={styles.addRow}>
        <input
          className={styles.addInput}
          placeholder="Nuovo task…"
          value={newTitle}
          onChange={e => setNewTitle(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && addTask()}
        />
        <button className={styles.addBtn} onClick={addTask} disabled={adding || !newTitle.trim()}>
          <Plus size={16} />
        </button>
      </div>

      {/* Filter tabs */}
      <div className={styles.tabs}>
        {['all', 'todo', 'overdue', 'done'].map(f => (
          <button
            key={f}
            className={`${styles.tab} ${filter === f ? styles.activeTab : ''}`}
            onClick={() => setFilter(f)}
          >
            {f === 'all' ? 'Tutti' : f === 'todo' ? 'Da fare' : f === 'overdue' ? 'Scaduti' : 'Completati'}
          </button>
        ))}
      </div>

      {/* Task list */}
      {loading ? (
        <div className={styles.empty}>Caricamento…</div>
      ) : filtered.length === 0 ? (
        <div className={styles.empty}>Nessun task</div>
      ) : (
        <div className={styles.list}>
          {filtered.map(t => {
            const isOverdue = t.status !== 'done' && t.due_date && new Date(t.due_date) < now;
            return (
              <div
                key={t.id}
                className={`${styles.card} ${t.status === 'done' ? styles.done : ''} ${isOverdue ? styles.overdue : ''} fade-in`}
              >
                <button
                  className={styles.checkBtn}
                  onClick={() => t.status !== 'done' && complete(t.id)}
                  disabled={t.status === 'done'}
                >
                  {t.status === 'done'
                    ? <CheckCircle2 size={18} className="text-green" />
                    : <Circle size={18} className="text-3" />
                  }
                </button>
                <div className={styles.info}>
                  <span className={styles.taskTitle}>{t.title}</span>
                  {t.due_date && (
                    <span className={`${styles.due} ${isOverdue ? 'text-red' : 'text-3'}`}>
                      {new Date(t.due_date).toLocaleDateString('it-IT')}
                    </span>
                  )}
                </div>
                <span className={`badge badge-${t.priority}`}>{t.priority}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
